import { getJourneyId } from './analytics';
import { isMetaCampaignTaggedVisit } from './metaCampaignTags';

type VitalName = 'booking_vital_lcp' | 'booking_vital_cls';

// One observation per metric per browser journey. Direct and QA visits without
// this Meta campaign's tags remain measurable in the other cohort.
const sentInMemory = new Set<string>();
let pendingRouteFlush: ReturnType<typeof setTimeout> | null = null;
function once(journeyId: string, name: VitalName, campaignTagged: boolean): boolean {
  // A visitor may inspect the page directly, then follow a campaign link in
  // the same tab. The direct sample must not suppress the campaign sample.
  const key = `ig_field_vital:${journeyId}:${campaignTagged ? 'campaign_tagged' : 'other'}:${name}`;
  if (sentInMemory.has(key)) return false;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch { /* Storage can be disabled. The in-memory set still guards this page. */ }
  sentInMemory.add(key);
  return true;
}

function sendVital(journeyId: string, name: VitalName, value: number, campaignTagged: boolean): void {
  if (!Number.isFinite(value) || value < 0 || !once(journeyId, name, campaignTagged)) return;
  const body = JSON.stringify({
    journey_id: journeyId,
    event_name: name,
    event_category: campaignTagged ? 'campaign_tagged' : 'other',
    metric_value: value,
  });
  if (typeof navigator.sendBeacon === 'function') {
    try {
      if (navigator.sendBeacon('/api/booking-event', new Blob([body], { type: 'application/json' }))) return;
    } catch { /* Fall through to keepalive fetch. */ }
  }
  fetch('/api/booking-event', {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => {});
}

export function observeBookingFieldVitals(params: URLSearchParams): () => void {
  if (typeof PerformanceObserver === 'undefined') return () => {};
  // React can immediately remount an effect in development, or when only the
  // query changes. In those cases the new observer takes over this document.
  if (pendingRouteFlush !== null) {
    clearTimeout(pendingRouteFlush);
    pendingRouteFlush = null;
  }
  const journeyId = getJourneyId();
  const campaignTagged = isMetaCampaignTaggedVisit(params);
  let lcp = 0;
  let cls = 0;
  let windowValue = 0;
  let windowStart = 0;
  let lastShift = 0;
  let sawLcp = false;
  let supportsCls = false;
  const observers: PerformanceObserver[] = [];

  const onLcp = (entries: PerformanceEntry[]): void => {
    for (const entry of entries) {
      lcp = entry.startTime;
      sawLcp = true;
    }
  };
  const onCls = (entries: PerformanceEntry[]): void => {
    for (const entry of entries) {
      if (!('value' in entry) || typeof entry.value !== 'number' ||
          !('hadRecentInput' in entry) || entry.hadRecentInput) continue;
      const time = entry.startTime;
      if (windowStart === 0 || time - lastShift > 1000 || time - windowStart > 5000) {
        windowStart = time;
        windowValue = 0;
      }
      lastShift = time;
      windowValue += entry.value;
      cls = Math.max(cls, windowValue);
    }
  };

  try {
    const observer = new PerformanceObserver((list) => onLcp(list.getEntries()));
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    observers.push(observer);
  } catch { /* The browser may not support this entry type. */ }

  try {
    const observer = new PerformanceObserver((list) => onCls(list.getEntries()));
    observer.observe({ type: 'layout-shift', buffered: true });
    supportsCls = true;
    observers.push(observer);
  } catch { /* The browser may not support this entry type. */ }

  function capturePending(): void {
    for (const observer of observers) {
      const records = observer.takeRecords();
      if (records[0]?.entryType === 'largest-contentful-paint') onLcp(records);
      if (records[0]?.entryType === 'layout-shift') onCls(records);
    }
  }

  function flush(): void {
    capturePending();
    if (sawLcp) sendVital(journeyId, 'booking_vital_lcp', lcp, campaignTagged);
    if (supportsCls) sendVital(journeyId, 'booking_vital_cls', cls, campaignTagged);
  }

  function onVisibility(): void {
    if (document.visibilityState === 'hidden') flush();
  }
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', flush);
    capturePending();
    for (const observer of observers) observer.disconnect();
    // A route change keeps the document alive, so pagehide never fires. Defer
    // the send one task to let an immediate remount cancel a premature sample.
    pendingRouteFlush = setTimeout(() => {
      pendingRouteFlush = null;
      flush();
    }, 0);
  };
}
